import React, { useMemo, useRef } from 'react';
import { Button, Checkbox, Form, Icon, Message } from 'semantic-ui-react';
import { getUpstreamModelSuggestionsV820 } from './channelModelVisibilityV820';
import {
  MODEL_RESOLUTIONS_V822,
  updateModelRuleRowV822,
  modelRuleErrorTextV1015,
} from './channelModelRulesV822';
import ChannelModelRulesTransferV822 from './ChannelModelRulesTransferV822';
import './ChannelModelMappingEditorV820.css';
import './ChannelModelRulesV822.css';

const ChannelModelMappingEditorV820 = ({
  rows,
  channelModels,
  modelIdPrefix,
  onChange,
  validation,
  t,
}) => {
  const nextRowId = useRef(0);
  const currentRows = Array.isArray(rows) ? rows : [];
  const upstreamSuggestions = useMemo(
    () => getUpstreamModelSuggestionsV820(channelModels, modelIdPrefix),
    [channelModels, modelIdPrefix]
  );

  const updateRow = (rowId, field, value) => {
    onChange(updateModelRuleRowV822(currentRows, rowId, field, value));
  };

  const appendLowcost = (row, event) => {
    const name = row.publicModel.trim() || row.upstreamModel.trim();
    if (!name) return;
    updateRow(
      row.id,
      'publicModel',
      name.endsWith('-lowcost') ? name : `${name}-lowcost`
    );
    event.currentTarget
      .closest('.channel-model-mapping-row-v820__public')
      ?.querySelector('input')
      ?.focus();
  };

  const addRow = () => {
    nextRowId.current += 1;
    onChange([
      ...(Array.isArray(rows) ? rows : []),
      {
        id: `mapping-new-${nextRowId.current}`,
        upstreamModel: '',
        publicModel: '',
        resolution: '',
        targetOnly: true,
        enabled: true,
      },
    ]);
  };

  const removeRow = (rowId) => {
    onChange(
      (Array.isArray(rows) ? rows : []).filter((row) => row.id !== rowId)
    );
  };

  return (
    <div className='field channel-model-mapping-editor-v820'>
      <div className='channel-model-mapping-editor-v820__header'>
        <label>{t('channel.edit.model_mapping')}</label>
        <Button
          type='button'
          size='small'
          icon
          labelPosition='left'
          onClick={addRow}
        >
          <Icon name='plus' />
          {t('channel.edit.model_mapping_add')}
        </Button>
      </div>

      <p>{t('channel.edit.model_rules_v822.parameter_notice')}</p>
      <ChannelModelRulesTransferV822
        rows={currentRows}
        activation
        selectedIds={currentRows.map((row) => row.id)}
        onSelectAll={() =>
          onChange(
            currentRows.map((row) => ({
              ...row,
              enabled: !currentRows.every((entry) => entry.enabled !== false),
            }))
          )
        }
        channelModels={channelModels}
        modelIdPrefix={modelIdPrefix}
        onChange={onChange}
        t={t}
      />

      <datalist id='channel-model-mapping-upstream-options-v820'>
        {upstreamSuggestions.map((modelName) => (
          <option key={modelName} value={modelName} />
        ))}
      </datalist>

      {(Array.isArray(rows) ? rows : []).map((row) => (
        <div className='channel-model-mapping-row-v820' key={row.id}>
          {validation?.error && validation.rowId === row.id && (
            <Message
              visible
              negative
              role='alert'
              className='channel-model-rule-error-v1015'
            >
              {modelRuleErrorTextV1015(validation, t)}
            </Message>
          )}
          <Form.Input
            error={Boolean(validation?.error && validation.rowId === row.id)}
            className='channel-model-mapping-row-v820__upstream'
            fluid
            list='channel-model-mapping-upstream-options-v820'
            label={t('channel.edit.model_mapping_upstream')}
            placeholder={t('channel.edit.model_mapping_upstream_placeholder')}
            value={row.upstreamModel}
            onChange={(event, { value }) =>
              updateRow(row.id, 'upstreamModel', value)
            }
          />
          <div
            className='channel-model-mapping-row-v820__arrow'
            aria-hidden='true'
          >
            <Icon name='long arrow alternate right' />
          </div>
          <div className='field channel-model-mapping-row-v820__public'>
            <div className='channel-model-mapping-row-v1016__public-header'>
              <label htmlFor={`${row.id}-public-v1016`}>
                {t('channel.edit.model_mapping_public')}
              </label>
              <Button
                type='button'
                size='mini'
                basic
                className='channel-model-mapping-row-v1016__lowcost'
                aria-label={t('channel.edit.model_mapping_lowcost_v1016')}
                title={t('channel.edit.model_mapping_lowcost_v1016')}
                disabled={!row.publicModel.trim() && !row.upstreamModel.trim()}
                onClick={(event) => appendLowcost(row, event)}
              >
                <Icon name='tag' />
                -lowcost
              </Button>
            </div>
            <Form.Input
              id={`${row.id}-public-v1016`}
              fluid
              placeholder={t('channel.edit.model_mapping_public_placeholder')}
              value={row.publicModel}
              onChange={(event, { value }) =>
                updateRow(row.id, 'publicModel', value)
              }
            />
          </div>
          <Button
            className='channel-model-mapping-row-v820__delete'
            type='button'
            basic
            color='red'
            icon='trash alternate outline'
            aria-label={t('channel.edit.model_mapping_delete')}
            title={t('channel.edit.model_mapping_delete')}
            onClick={() => removeRow(row.id)}
          />
          <Checkbox
            className='channel-model-mapping-row-v820__visibility'
            toggle
            checked={Boolean(row.targetOnly)}
            label={t('channel.edit.model_mapping_hide_upstream')}
            onChange={(event, { checked }) =>
              updateRow(row.id, 'targetOnly', checked)
            }
          />
          <Checkbox
            className='channel-model-mapping-row-v107__enabled'
            checked={row.enabled !== false}
            label={t('channel.edit.model_rules_v107.enabled')}
            aria-label={t('channel.edit.model_rules_v107.enable_rule', {
              model: row.publicModel,
            })}
            onChange={(event, { checked }) =>
              updateRow(row.id, 'enabled', checked)
            }
          />
          <div className='field channel-model-mapping-row-v820__parameter'>
            <label htmlFor={`${row.id}-parameter-v822`}>
              {t('channel.edit.model_rules_v822.parameter')}
            </label>
            <select
              id={`${row.id}-parameter-v822`}
              value='resolution'
              onChange={() => {}}
            >
              <option value='resolution'>
                {t('channel.edit.model_rules_v822.resolution')}
              </option>
            </select>
          </div>
          <div className='field channel-model-mapping-row-v820__resolution'>
            <label htmlFor={`${row.id}-resolution-v822`}>
              {t('channel.edit.model_rules_v822.value')}
            </label>
            <select
              id={`${row.id}-resolution-v822`}
              value={row.resolution || ''}
              onChange={(event) =>
                updateRow(row.id, 'resolution', event.target.value)
              }
            >
              {MODEL_RESOLUTIONS_V822.map((resolution) => (
                <option key={resolution} value={resolution}>
                  {resolution === '512'
                    ? '0.5K'
                    : resolution ||
                      t('channel.edit.model_rules_v822.unconstrained')}
                </option>
              ))}
              {!MODEL_RESOLUTIONS_V822.includes(row.resolution || '') && (
                <option value={row.resolution}>
                  {t('channel.edit.model_rules_v822.errors.invalid_resolution')}
                </option>
              )}
            </select>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ChannelModelMappingEditorV820;
